import React, { useState } from 'react';
import styles from './index.module.scss';
import { Button, Card, Typography } from '@navi/web-ui/lib/primitives';
import { ArrowRightIcon } from '@navi/web-ui/lib/icons';
import CardHeader from './CardHeader';
import AddressCardFeedback from './AddressCardFeedback';
import SimilarAddressDrawer from './SimilarAddressDrawer';
import { AddressCardProps } from './interfaces';
import LonghornInfoPopper from './AddressInfoPopover/LonghornInfoPopper';
import { useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import { Roles } from 'src/pages/auth/constants/AuthConstants';
import ProofImageModal from './AddressInfoPopover/ProofImageModal';

const AddressCard: React.FC<AddressCardProps> = props => {
  const { address } = props;
  const { city, addressText, state, latestFeedback, relatedLocationIds } = address;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProofModalOpen, setIsProofModalOpen] = useState(false);
  const [proofModalData, setProofModalData] = useState<{
    source: string;
    proofOfSourceUrl: string;
  } | null>(null);

  const user = useSelector((state: RootState) => state.common.userData);
  const hasGlobalAccess = user?.roles?.includes(Roles.ROLE_GLOBAL_ACCESS);

  const addressLine = [addressText, city, state].filter(line => !!line).join(', ');

  const handleToggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleViewProof = (source: string, proofOfSourceUrl: string) => {
    setProofModalData({
      source: source || 'Address Proof',
      proofOfSourceUrl
    });
    setIsProofModalOpen(true);
  };

  const handleCloseProofModal = () => {
    setIsProofModalOpen(false);
    setProofModalData(null);
  };

  return (
    <>
      <div className={styles.cardWrapper}>
        <div className={styles.cardNumber}>
          <Typography variant="h5" color="var(--grayscale-content-2)">
            {address.rank}
          </Typography>
        </div>
        <Card className={styles.addressCard}>
          <CardHeader address={address} />
          <AddressCardFeedback
            latestFeedback={latestFeedback}
            addressLine={addressLine}
            relatedLocationIds={relatedLocationIds}
          />
          <div className={styles.addressLine}>
            <Typography variant="h4" className="rr-block">
              {addressLine}
            </Typography>
            <Button
              variant="text"
              onClick={handleToggleModal}
              size="small"
              disabled={!address?.similarLocationEntities?.length}
              endAdornment={<ArrowRightIcon color="var(--navi-color-blue-base)" />}
              className="justify-start"
            >
              Similar addresses
            </Button>
          </div>
          {address.source === 'LONGHORN_CIU' && hasGlobalAccess && (
            <LonghornInfoPopper
              addressEntity={address}
              onViewProof={handleViewProof}
              buttonClassName="inline-flex items-center justify-center bg-transparent border-none cursor-pointer px-4 pb-4"
            />
          )}
        </Card>
      </div>
      <SimilarAddressDrawer
        isOpen={isModalOpen}
        onClose={handleToggleModal}
        address={address}
        onViewProof={handleViewProof}
      />
      <ProofImageModal
        isOpen={isProofModalOpen}
        onClose={handleCloseProofModal}
        proofOfSourceUrl={proofModalData?.proofOfSourceUrl || ''}
        source={proofModalData?.source || address.secondarySource || 'Address Proof'}
      />
    </>
  );
};

export default AddressCard;
